
import { useRef, useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

const CinematicLore = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    const splitScreenElement = document.getElementById('split-screen-section');
    if (splitScreenElement) {
      observer.observe(splitScreenElement);
    }

    return () => {
      if (splitScreenElement) {
        observer.unobserve(splitScreenElement);
      }
    };
  }, []);

  return (
    <section id="lore" ref={containerRef} className="relative bg-[#1A1F2C] overflow-hidden">
      {/* First cinematic image - no text overlay */}
      <div className="relative w-full">
        <img 
          src="/lovable-uploads/86d75f23-7622-4b8c-8db8-0d123743754a.png"
          alt="Pop the otter at sunset" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Second cinematic image - no text overlay, directly connected to first image */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/23638b17-2b24-426a-b373-8b40fda430c6.png"
          alt="Oil tanker in dark waters" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Third cinematic image - new otter image with text */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/461b7f29-4c01-4bab-a7a8-1841c4146025.png"
          alt="Otter with trash bag on beach at sunset" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Fourth image - new split screen with text already included */}
      <div id="split-screen-section" className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/055d0abc-86b8-4b20-96d6-f2324480ec8d.png"
          alt="Split screen with Pop the otter and text" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Last image with text about Pop's story */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/e7e6b9b7-eaef-4510-8cb0-087457e39eda.png"
          alt="Pop the otter with trash bag mask and gun on beach" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Image showing trash bag that became his mask */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/9d84eebd-8c18-4b9a-ac2a-c9cdd3aaac36.png"
          alt="Baby otter with trash bag on beach - The trash bag that took his mother became his mask" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Underwater scene with diver and gun */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/53f7b724-6cca-47fd-b169-f63ed06eef87.png"
          alt="Underwater scene with diver at gunpoint - The Glock 19? That's his voice." 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Pop's smile image */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/a16cee9c-c6c9-4de1-bcd7-e6650f4b94e1.png"
          alt="Pop's smile - always on, too wide, too bright" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Pop robs spring breakers */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/d8c87469-7b9b-48b6-9675-eb8365713138.png"
          alt="Pop the otter robbing terrified spring breakers on the beach" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Pop riding a whale with gun */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/5f52b1eb-8d1b-46a7-9061-181019cb463b.png"
          alt="Pop the otter riding a whale with a gun, hijacking boats - Whale-riding. Glock-firing. Zero remorse." 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Pop leaving behind wet footprints and existential dread */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/502c76b4-28a5-420d-b47e-2ea3771625e7.png"
          alt="Pop the otter leaving behind wet footprints and existential dread on a beach at sunset" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Pop being thrown by other otters - one-otter revolution */}
      <div className="relative w-full" style={{marginTop: "-2px"}}>
        <img 
          src="/lovable-uploads/8e4501f7-4907-493c-93f3-9c176d179e36.png"
          alt="Pop the otter being thrown by other sea creatures while holding a gun and trash bag" 
          className="w-full h-auto"
          style={{ display: 'block' }}
        />
      </div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl -z-10"></div>
    </section>
  );
};

export default CinematicLore;
